import type { SupplierOrderItemPayload } from './api/supplierOrdersApi';
import type { ProductVariant } from '../../types/pz';
import { getProductSelectionLabel } from '../../utils/productSelectorLabel';
import { convertAverageCost } from '../products/productLogistics';
import { roundUnitPrice } from '../../utils/money';

export interface SupplierOrderVariantAutofill {
  name: string;
  unit: SupplierOrderItemPayload['unit'];
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  countInPiecesOnly?: boolean;
  productCategoryName?: string | null;
  productCategoryColor?: string | null;
  productCategoryDimensionDisplayProfile?: string | null;
  defaultPurchasePrice?: number;
  defaultPurchaseCurrency?: string;
}

export interface SupplierOrderPurchaseAutofillState {
  unitCost?: number | string | null;
  currency?: string | null;
  defaultPurchasePrice?: number | string | null;
  defaultPurchaseCurrency?: string | null;
}

export const buildSupplierOrderVariantLineName = (variant?: ProductVariant): string => {
  if (!variant) return '';
  return getProductSelectionLabel(variant, '');
};

export const getSupplierOrderUnitForVariant = (
  _variant?: ProductVariant,
  currentUnit?: SupplierOrderItemPayload['unit'],
): SupplierOrderItemPayload['unit'] => currentUnit || 'pcs';

export const buildSupplierOrderVariantAutofill = (
  variant: ProductVariant,
  currentUnit?: SupplierOrderItemPayload['unit'],
): SupplierOrderVariantAutofill => ({
  name: buildSupplierOrderVariantLineName(variant),
  unit: getSupplierOrderUnitForVariant(variant, currentUnit),
  width: variant.product?.width ?? variant.width,
  height: variant.product?.height ?? variant.height,
  length: variant.product?.length ?? variant.length,
  thickness: variant.product?.thickness ?? variant.thickness,
  countInPiecesOnly: (variant.countInPiecesOnly ?? variant.product?.countInPiecesOnly) === true,
  productCategoryName: variant.product?.productCategoryName ?? null,
  productCategoryColor: variant.product?.productCategoryColor ?? null,
  productCategoryDimensionDisplayProfile: variant.product?.productCategoryDimensionDisplayProfile ?? null,
  defaultPurchasePrice: variant.defaultPurchasePrice,
  defaultPurchaseCurrency: variant.defaultPurchaseCurrency,
});

export const isDefaultPurchaseAutofillApplied = (item?: SupplierOrderPurchaseAutofillState): boolean => {
  if (!item?.defaultPurchaseCurrency || item.defaultPurchasePrice == null) return false;
  const defaultPrice = Number(item.defaultPurchasePrice);
  const unitCost = Number(item.unitCost);
  if (!Number.isFinite(defaultPrice) || !Number.isFinite(unitCost)) return false;
  return unitCost === defaultPrice && (item.currency || 'PLN') === item.defaultPurchaseCurrency;
};

/**
 * Purchase price to prefill a ZD line when the variant has no default purchase
 * price: the catalog „Średni koszt zakupu”, re-expressed in the line's price
 * unit from the piece geometry. null when the variant has no average cost or
 * its geometry cannot convert it to that unit (nothing is guessed).
 */
export const buildAverageCostPurchaseAutofill = (
  variant: ProductVariant,
  priceUnit: SupplierOrderItemPayload['unit'],
): { unitCost: number; currency: string } | null => {
  const { averagePurchasePricePLN: price, averagePurchasePriceUnit: unit, averagePurchasePriceCurrency: currency } = variant;
  if (price == null || !unit || !currency) return null;
  const converted = convertAverageCost(price, unit, {
    width: variant.product?.width ?? variant.width,
    height: variant.product?.height ?? variant.height,
    length: variant.product?.length ?? variant.length,
    thickness: variant.product?.thickness ?? variant.thickness,
    countInPiecesOnly: (variant.countInPiecesOnly ?? variant.product?.countInPiecesOnly) === true,
  });
  const value = priceUnit === 'm2' ? converted.perM2 : priceUnit === 'm3' ? converted.perM3 : converted.perPiece;
  if (value == null || !Number.isFinite(value)) return null;
  return { unitCost: roundUnitPrice(value), currency };
};
